import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [serverStatus, setServerStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLines, setLogsLines] = useState(100);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      verifyToken(token);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchServerStatus();
      fetchLogs();
      const interval = setInterval(() => {
        fetchServerStatus();
        fetchLogs();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, logsLines]);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setError('');
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/login`, {
        username,
        password
      });
      
      localStorage.setItem('admin_token', response.data.access_token);
      setIsAuthenticated(true);
      setError('');
    } catch (error) {
      setError('Identifiants incorrects');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchServerStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/party-server/status`, {
        headers: getAuthHeaders()
      });
      setServerStatus(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération du statut:', error);
    }
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/party-server/logs?lines=${logsLines}`, {
        headers: getAuthHeaders()
      });
      setLogs(response.data.logs);
    } catch (error) {
      console.error('Erreur lors de la récupération des logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleRestart = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir redémarrer le party server ?')) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/party-server/restart`, {}, {
        headers: getAuthHeaders()
      });
      alert('Party server redémarré avec succès');
      setTimeout(() => fetchServerStatus(), 2000);
    } catch (error) {
      alert('Erreur lors du redémarrage: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStop = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir arrêter le party server ?')) {
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/admin/party-server/stop`, {}, {
        headers: getAuthHeaders()
      });
      alert('Party server arrêté avec succès');
      setTimeout(() => fetchServerStatus(), 2000);
    } catch (error) {
      alert('Erreur lors de l\'arrêt: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStart = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/party-server/start`, {}, {
        headers: getAuthHeaders()
      });
      alert('Party server démarré avec succès');
      setTimeout(() => fetchServerStatus(), 2000);
    } catch (error) {
      alert('Erreur lors du démarrage: ' + (error.response?.data?.detail || error.message));
    }
  };

  const formatUptime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}j ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}min`;
    if (minutes > 0) return `${minutes}min ${seconds % 60}s`;
    return `${seconds}s`;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 max-w-md w-full">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
            Administration
          </h1>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nom d'utilisateur
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required
              />
            </div>
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 text-red-700 dark:text-red-300 rounded">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
              Administration - Party Server
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Gestion du serveur de party Minecraft
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Déconnexion
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Statut du serveur
            </h2>
            {serverStatus ? (
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-gray-600 dark:text-gray-400 w-32">Statut:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    serverStatus.running
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {serverStatus.running ? 'En ligne' : 'Hors ligne'}
                  </span>
                </div>
                {serverStatus.running && (
                  <>
                    <div className="flex items-center">
                      <span className="text-gray-600 dark:text-gray-400 w-32">CPU:</span>
                      <span className="text-gray-900 dark:text-white">{serverStatus.cpu?.toFixed(1)}%</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-600 dark:text-gray-400 w-32">Mémoire:</span>
                      <span className="text-gray-900 dark:text-white">{(serverStatus.memory / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-600 dark:text-gray-400 w-32">Uptime:</span>
                      <span className="text-gray-900 dark:text-white">{formatUptime(serverStatus.uptime)}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-600 dark:text-gray-400 w-32">Redémarrages:</span>
                      <span className="text-gray-900 dark:text-white">{serverStatus.restarts}</span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">Chargement...</p>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Actions
            </h2>
            <div className="space-y-3">
              {serverStatus?.running ? (
                <>
                  <button
                    onClick={handleRestart}
                    className="w-full px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
                  >
                    Redémarrer
                  </button>
                  <button
                    onClick={handleStop}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Arrêter
                  </button>
                </>
              ) : (
                <button
                  onClick={handleStart}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Démarrer
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Logs du serveur
            </h2>
            <div className="flex items-center gap-4">
              <label className="text-sm text-gray-600 dark:text-gray-400">
                Lignes:
                <input
                  type="number"
                  value={logsLines}
                  onChange={(e) => setLogsLines(parseInt(e.target.value) || 100)}
                  min="10"
                  max="1000"
                  className="ml-2 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-white w-20"
                />
              </label>
              <button
                onClick={fetchLogs}
                disabled={logsLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {logsLoading ? 'Chargement...' : 'Actualiser'}
              </button>
            </div>
          </div>
          <div className="bg-gray-900 text-green-400 font-mono text-sm p-4 rounded overflow-auto max-h-96">
            {logs.length > 0 ? (
              logs.map((log, index) => (
                <div key={index} className="mb-1">
                  {log}
                </div>
              ))
            ) : (
              <div className="text-gray-500">Aucun log disponible</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;

